import React, { useState, useEffect } from "react";
import {
  Page,
  LegacyCard,
  Text,
  Button,
  TextField,
  Select,
  Popover,
  ChoiceList,
  FormLayout,
  Banner,
  Spinner,
  IndexTable,
  BlockStack,
  InlineGrid,
  Card,
} from "@shopify/polaris";
import { SettingsIcon } from "@shopify/polaris-icons";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx/xlsx.mjs";
import AppWrapper from "../AppWrapper";
import { getReport } from "./registry";
import DateRangeFilter from "./DateRangeFilter";
import {
  applyColumnFormats,
  toNumber,
  toExcelSerial,
  EXPORT_TYPE_FMT,
  DECIMAL_FMT,
  DATE_FMT,
} from "../../utility/xlsxExport";

// Distinct, sorted {label,value} options for a "facet" filter, derived from the
// values a column takes across the already-fetched rows. Blanks are dropped.
// `order` (optional array) pins a custom sequence — values in it sort first in
// that order, everything else falls back to alphabetical after them.
const distinctOptions = (rows, col, order) => {
  const set = new Set();
  for (const r of rows) {
    const v = r[col];
    if (v !== null && v !== undefined && v !== "") set.add(String(v));
  }
  const hasOrder = Array.isArray(order) && order.length > 0;
  const rank = (v) => {
    const i = hasOrder ? order.indexOf(v) : -1;
    return i === -1 ? (hasOrder ? order.length : 0) : i;
  };
  return [...set]
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b, "es"))
    .map((v) => ({ label: v, value: v }));
};

// Lowercase + strip accents so "comunicacion" matches "COMUNICACIÓN".
const normalizeText = (v) =>
  String(v ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Client-side multi-select facet: narrows the loaded rows without re-running the
// RPC. Empty selection = show everything. Reusable across reports via a filter
// definition of shape { type:"facet", key, label, column?, options? }.
function FacetFilter({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const count = selected?.length || 0;
  return (
    <Popover
      active={open}
      preferredAlignment="left"
      activator={
        <Button disclosure onClick={() => setOpen((o) => !o)}>
          {label + (count ? ` (${count})` : "")}
        </Button>
      }
      onClose={() => setOpen(false)}
    >
      <div style={{ padding: "0.75rem 1rem", minWidth: 220, maxHeight: 320, overflowY: "auto" }}>
        <ChoiceList
          allowMultiple
          title={label}
          titleHidden
          choices={options}
          selected={selected || []}
          onChange={onChange}
        />
        {count > 0 && (
          <div style={{ marginTop: 8 }}>
            <Button variant="plain" onClick={() => onChange([])}>
              Limpiar
            </Button>
          </div>
        )}
      </div>
    </Popover>
  );
}

// Per-report hidden-column preference (stored as the list of hidden keys, so
// columns added to a report later default to visible).
const hiddenColsKey = (id) => `report_hidden_cols_${id}`;
const loadHiddenCols = (id) => {
  try {
    const raw = localStorage.getItem(hiddenColsKey(id));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

function Report() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const report = getReport(reportId);

  const [filterValues, setFilterValues] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasRun, setHasRun] = useState(false);
  const [sortColumnIndex, setSortColumnIndex] = useState(null);
  const [sortDirection, setSortDirection] = useState("descending");
  // Options for "select" filters loaded via their loadOptions() thunk, keyed
  // by filter key. Static options (f.options) don't go through here.
  const [dynOptions, setDynOptions] = useState({});
  const [hiddenCols, setHiddenCols] = useState(() => loadHiddenCols(reportId));
  const [colsPopoverOpen, setColsPopoverOpen] = useState(false);
  // Collapsible groups (only meaningful while a `groupable` column drives the
  // sort). `groupsOpenBase` is the blanket state — null means "use the report's
  // collapseGroups default" — and `toggledGroups` holds the group values the
  // user flipped away from it, so expanding one client doesn't disturb the rest.
  const [groupsOpenBase, setGroupsOpenBase] = useState(null);
  const [toggledGroups, setToggledGroups] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    // Reset per-report view state when navigating between reports, applying the
    // report's defaultSort (by column key) against its currently-visible columns.
    const hidden = loadHiddenCols(reportId);
    setHiddenCols(hidden);
    const ds = report?.defaultSort;
    const dsIndex = ds
      ? (report.columns || [])
          .filter((c) => !hidden.includes(c.key))
          .findIndex((c) => c.key === ds.key)
      : -1;
    setSortColumnIndex(dsIndex >= 0 ? dsIndex : null);
    setSortDirection(ds?.direction || "descending");
    setGroupsOpenBase(null);
    setToggledGroups(new Set());
    (async () => {
      for (const f of report?.filters || []) {
        if (f.type === "select" && typeof f.loadOptions === "function") {
          try {
            const opts = await f.loadOptions();
            if (!cancelled) setDynOptions((p) => ({ ...p, [f.key]: opts || [] }));
          } catch (e) {
            console.error(`No se pudieron cargar opciones de ${f.key}:`, e);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Columns actually shown (table reports only). Stored as hidden keys so new
  // columns default visible; always keep at least one column.
  const visibleColumns = (report?.columns || []).filter(
    (c) => !hiddenCols.includes(c.key)
  );

  // Rows actually shown: raw fetched rows narrowed by any client-side "facet"
  // filters (include semantics — empty selection passes everything) and "search"
  // filters (accent/case-insensitive substring over f.columns). This drives
  // the table, summaries and export; facet OPTIONS still come from the raw rows
  // so selecting one value doesn't remove the others from the picker.
  const displayRows = rows.filter((r) =>
    (report?.filters || []).every((f) => {
      if (f.type === "search") {
        const q = normalizeText(filterValues[f.key]).trim();
        if (!q) return true;
        return (f.columns || [f.key]).some((c) => normalizeText(r[c]).includes(q));
      }
      if (f.type !== "facet") return true;
      const sel = filterValues[f.key];
      if (!Array.isArray(sel) || sel.length === 0) return true;
      return sel.includes(String(r[f.column || f.key]));
    })
  );

  const handleColsChange = (selectedKeys) => {
    if (!selectedKeys.length) return; // never hide everything
    const hidden = (report.columns || [])
      .map((c) => c.key)
      .filter((k) => !selectedKeys.includes(k));
    setHiddenCols(hidden);
    setSortColumnIndex(null); // indices shift when columns change
    try {
      localStorage.setItem(hiddenColsKey(reportId), JSON.stringify(hidden));
    } catch {
      // ignore storage errors
    }
  };

  if (!report) {
    return (
      <AppWrapper>
        <Page
          title="Reporte no encontrado"
          backAction={{ onAction: () => navigate("/reports") }}
        >
          <LegacyCard sectioned>
            <Text>No existe un reporte con id "{reportId}".</Text>
          </LegacyCard>
        </Page>
      </AppWrapper>
    );
  }

  const setFilter = (key, value) =>
    setFilterValues((prev) => ({ ...prev, [key]: value }));

  const setDateRange = (fromKey, toKey, range) =>
    setFilterValues((prev) => ({ ...prev, [fromKey]: range.from, [toKey]: range.to }));

  const canRun = (report.filters || []).every((f) => {
    if (!f.required) return true;
    if (f.type === "daterange") {
      return filterValues[f.fromKey] && filterValues[f.toKey];
    }
    return filterValues[f.key] && String(filterValues[f.key]).length > 0;
  });

  const runReport = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await report.fetch(filterValues);
      setRows(Array.isArray(data) ? data : []);
      setHasRun(true);
      setGroupsOpenBase(null);
      setToggledGroups(new Set());
    } catch (e) {
      console.error(e);
      setError(e.message || "Error ejecutando el reporte");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCell = (col, row) => {
    const raw = row[col.key];
    if (col.format) return col.format(raw, row);
    if (raw === null || raw === undefined) return "";
    return String(raw);
  };

  // Build rows-of-cells for export and table render. For "pivot" reports we
  // synthesize a matrix (rowLabels × colLabels) plus row/column totals from
  // the long-format result the RPC returns: [{rowKey, colKey, value}, ...].
  const buildPivot = () => {
    if (!report.pivot) return null;
    const { rowKey, colKey, valueKey, rowOrder, colOrder, format, aggregator, showTotals } = report.pivot;
    const agg = aggregator || "sum";
    // rowOrder/colOrder pin the sequence of the keys we expect. Any key the RPC
    // actually returned that isn't pinned gets appended instead of dropped —
    // otherwise a bucket like "Otros" (an unclassified unit) disappears from
    // the table while still counting toward the totals, and the columns stop
    // adding up.
    const pinned = (order, key) => {
      const seen = [...new Set(displayRows.map((r) => r[key]))].filter(
        (v) => v !== null && v !== undefined && v !== ""
      );
      if (!order || !order.length) return seen;
      const extra = seen.filter((v) => !order.includes(v));
      if (!extra.length) return order;
      // Keep a trailing "Total" (RPC-supplied, see showTotals) last.
      const tail = order[order.length - 1] === "Total" ? 1 : 0;
      return [...order.slice(0, order.length - tail), ...extra, ...order.slice(order.length - tail)];
    };
    const rows_ = pinned(rowOrder, rowKey);
    const cols_ = pinned(colOrder, colKey);
    const grid = {};
    for (const r of displayRows) {
      const rk = r[rowKey];
      const ck = r[colKey];
      const v = Number(r[valueKey]) || 0;
      grid[rk] = grid[rk] || {};
      grid[rk][ck] = (grid[rk][ck] || 0) + v;
    }
    const fmt = format || ((v) => v);
    const aggregate = (vals) => {
      if (vals.length === 0) return 0;
      if (agg === "avg") return vals.reduce((a, b) => a + b, 0) / vals.length;
      return vals.reduce((a, b) => a + b, 0);
    };
    const colTotals = {};
    cols_.forEach((ck) => {
      colTotals[ck] = aggregate(rows_.map((rk) => grid[rk]?.[ck] || 0));
    });
    const dataRows = rows_.map((rk) => {
      const cells = cols_.map((ck) => grid[rk]?.[ck] || 0);
      const rowTotal = aggregate(cells);
      return { label: rk, cells, rowTotal };
    });
    const totalRow = {
      label: agg === "avg" ? "Promedio" : "Total",
      cells: cols_.map((ck) => colTotals[ck]),
      rowTotal: aggregate(Object.values(colTotals)),
    };
    return { rows: dataRows, totalRow, cols: cols_, fmt, showTotals: showTotals !== false };
  };

  const exportXlsx = () => {
    const ts = new Date().toISOString().substring(0, 10);
    if (report.pivot) {
      const p = buildPivot();
      if (!p) return;
      const header = [report.pivot.rowLabel || "", ...p.cols, "Total"];
      const data = [
        ...p.rows.map((r) => [r.label, ...r.cells, r.rowTotal]),
        [p.totalRow.label, ...p.totalRow.cells, p.totalRow.rowTotal],
      ];
      const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
      // Every cell but the row label is a measure — give them all the pivot's
      // declared number format so the sheet stays summable.
      const z =
        report.pivot.exportNumFmt ||
        EXPORT_TYPE_FMT[report.pivot.exportType] ||
        DECIMAL_FMT;
      const fmts = {};
      for (let c = 1; c <= p.cols.length + 1; c++) fmts[c] = z;
      applyColumnFormats(ws, fmts);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reporte");
      XLSX.writeFile(wb, `${report.id}-${ts}.xlsx`);
      return;
    }
    const headers = visibleColumns.map((c) => c.label);
    const data = displayRows.map((r) =>
      visibleColumns.map((c) => {
        const raw = r[c.key];
        // exportType "date" bypasses the on-screen formatter so the sheet gets a
        // true date cell (sortable/filterable) instead of pre-formatted text.
        if (c.exportType === "date") return toExcelSerial(raw) ?? "";
        // Every numeric exportType ("currency", "number", "integer",
        // "percent", …) ships the raw value so the sheet gets a real numeric
        // cell you can sum, instead of a pre-formatted "$1,234" string. The
        // currency symbol / decimals come from the cell format applied below.
        if (EXPORT_TYPE_FMT[c.exportType]) {
          const n = toNumber(raw);
          return n === null ? "" : n;
        }
        if (c.format) return c.format(raw, r);
        return raw ?? "";
      })
    );
    const aoa = [headers, ...data];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Give each date column its date format and each numeric column its number
    // format, so Excel renders them as dates / currency while keeping them
    // sortable and summable.
    const fmts = {};
    visibleColumns.forEach((c, i) => {
      const z =
        c.exportNumFmt ||
        (c.exportType === "date" ? DATE_FMT : EXPORT_TYPE_FMT[c.exportType]);
      if (z) fmts[i] = z;
    });
    applyColumnFormats(ws, fmts);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reporte");
    XLSX.writeFile(wb, `${report.id}-${ts}.xlsx`);
  };

  return (
    <AppWrapper>
      <Page
        title={report.title}
        subtitle={report.description}
        backAction={{ onAction: () => navigate("/reports") }}
        primaryAction={
          hasRun && displayRows.length > 0
            ? { content: "Exportar XLSX", onAction: exportXlsx }
            : undefined
        }
      >
        <BlockStack gap="400">
          <LegacyCard sectioned>
            <FormLayout>
              <FormLayout.Group>
                {(report.filters || []).map((f) => {
                  // Facet filters are client-side post-filters — rendered in the
                  // results header over the loaded rows, not here in the run bar.
                  if (f.type === "facet" || f.type === "search") return null;
                  if (f.type === "daterange") {
                    return (
                      <DateRangeFilter
                        key={f.key || `${f.fromKey}-${f.toKey}`}
                        label={f.label + (f.required ? " *" : "")}
                        from={filterValues[f.fromKey] || ""}
                        to={filterValues[f.toKey] || ""}
                        onChange={(range) => setDateRange(f.fromKey, f.toKey, range)}
                      />
                    );
                  }
                  if (f.type === "select") {
                    const opts = f.options || dynOptions[f.key] || [];
                    return (
                      <Select
                        key={f.key}
                        label={f.label + (f.required ? " *" : "")}
                        options={[
                          { label: f.placeholder || "Todos", value: "" },
                          ...opts,
                        ]}
                        value={filterValues[f.key] || ""}
                        onChange={(v) => setFilter(f.key, v)}
                      />
                    );
                  }
                  return (
                    <TextField
                      key={f.key}
                      label={f.label + (f.required ? " *" : "")}
                      type={f.type || "text"}
                      value={filterValues[f.key] || ""}
                      onChange={(v) => setFilter(f.key, v)}
                      autoComplete="off"
                    />
                  );
                })}
              </FormLayout.Group>
              <Button
                variant="primary"
                onClick={runReport}
                loading={loading}
                disabled={!canRun || loading}
              >
                Ejecutar
              </Button>
            </FormLayout>
          </LegacyCard>

          {error && <Banner tone="critical">{error}</Banner>}

          {loading && (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spinner size="small" />
            </div>
          )}

          {!loading && hasRun && displayRows.length > 0 && report.summary && report.summary.length > 0 && (
            <BlockStack gap="300">
              {(() => {
                // Pass 1: collect all "stat card" summaries (count + groupBy) into
                // a single responsive grid: total first, then one card per group value.
                const statCards = [];
                const otherSummaries = [];
                const fmtVal = (s, v) => (s.valueFormat ? s.valueFormat(v) : v);
                for (const s of report.summary) {
                  if (s.type === "count") {
                    statCards.push({
                      key: `count-${s.label}`,
                      label: s.label,
                      value: fmtVal(s, displayRows.length),
                      tone: "primary",
                    });
                  } else if (s.type === "sum") {
                    const total = displayRows.reduce((acc, r) => acc + (Number(r[s.valueKey]) || 0), 0);
                    statCards.push({
                      key: `sum-${s.label}`,
                      label: s.label,
                      value: fmtVal(s, total),
                      tone: "primary",
                    });
                  } else if (s.type === "groupBy") {
                    const agg = {};
                    for (const r of displayRows) {
                      const raw = r[s.key];
                      const k = (raw === null || raw === undefined || raw === "") ? "—" : String(raw);
                      const inc = s.valueKey ? (Number(r[s.valueKey]) || 0) : 1;
                      agg[k] = (agg[k] || 0) + inc;
                    }
                    const entries = Object.entries(agg).sort((a, b) => b[1] - a[1]);
                    for (const [k, v] of entries) {
                      const display = s.format ? s.format(k === "—" ? null : k) : k;
                      statCards.push({
                        key: `${s.label}-${k}`,
                        label: display,
                        sublabel: s.label,
                        value: fmtVal(s, v),
                      });
                    }
                  } else {
                    otherSummaries.push(s);
                  }
                }
                return (
                  <>
                    {statCards.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          overflowX: "auto",
                          paddingBottom: 8,
                          alignItems: "stretch",
                        }}
                      >
                        {statCards.map((c) => (
                          <div
                            key={c.key}
                            style={{
                              flex: "0 0 180px",
                              width: 180,
                              height: 120,
                              padding: "12px 8px",
                              boxSizing: "border-box",
                              background: "var(--p-color-bg-surface)",
                              border: "1px solid var(--p-color-border)",
                              borderRadius: 12,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              textAlign: "center",
                              overflow: "hidden",
                            }}
                          >
                            <Text variant="headingXl" as="p">{c.value}</Text>
                            <div
                              style={{
                                width: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={String(c.label)}
                            >
                              <Text variant="bodySm" fontWeight={c.tone === "primary" ? "semibold" : undefined} truncate>
                                {c.label}
                              </Text>
                            </div>
                            {c.sublabel && (
                              <Text variant="bodySm" tone="subdued">{c.sublabel}</Text>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </BlockStack>
          )}

          {!loading && hasRun && report.pivot && displayRows.length > 0 && (() => {
            const p = buildPivot();
            if (!p) return null;
            return (
              <LegacyCard sectioned>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f4f4f4", textAlign: "left" }}>
                      <th style={{ padding: "8px 10px", borderBottom: "1px solid #ccc" }}>{report.pivot.rowLabel || ""}</th>
                      {p.cols.map((c) => (
                        <th key={c} style={{ padding: "8px 10px", borderBottom: "1px solid #ccc", textAlign: "right" }}>{c}</th>
                      ))}
                      {p.showTotals && (
                        <th style={{ padding: "8px 10px", borderBottom: "1px solid #ccc", textAlign: "right" }}>{p.totalRow.label}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {p.rows.map((r) => (
                      <tr key={r.label} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{ padding: "8px 10px" }}>{r.label}</td>
                        {r.cells.map((v, i) => (
                          <td key={i} style={{ padding: "8px 10px", textAlign: "right" }}>{p.fmt(v)}</td>
                        ))}
                        {p.showTotals && (
                          <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>{p.fmt(r.rowTotal)}</td>
                        )}
                      </tr>
                    ))}
                    {p.showTotals && (
                      <tr style={{ borderTop: "2px solid #999", background: "#fafafa" }}>
                        <td style={{ padding: "8px 10px", fontWeight: 700 }}>{p.totalRow.label}</td>
                        {p.totalRow.cells.map((v, i) => (
                          <td key={i} style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{p.fmt(v)}</td>
                        ))}
                        <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{p.fmt(p.totalRow.rowTotal)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </LegacyCard>
            );
          })()}

          {!loading && hasRun && !report.pivot && (
            <LegacyCard>
              <div
                style={{
                  padding: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <Text variant="bodySm" tone="subdued">
                  {displayRows.length} resultado{displayRows.length === 1 ? "" : "s"}
                  {displayRows.length !== rows.length ? ` de ${rows.length}` : ""}
                </Text>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {(report.filters || [])
                    .filter((f) => f.type === "search")
                    .map((f) => (
                      <div key={f.key} style={{ minWidth: 240 }}>
                        <TextField
                          label={f.label}
                          labelHidden
                          placeholder={f.placeholder || `Buscar ${f.label.toLowerCase()}…`}
                          value={filterValues[f.key] || ""}
                          onChange={(v) => setFilter(f.key, v)}
                          clearButton
                          onClearButtonClick={() => setFilter(f.key, "")}
                          autoComplete="off"
                        />
                      </div>
                    ))}
                  {(report.filters || [])
                    .filter((f) => f.type === "facet")
                    .map((f) => (
                      <FacetFilter
                        key={f.key}
                        label={f.label}
                        options={f.options || distinctOptions(rows, f.column || f.key, f.order)}
                        selected={filterValues[f.key] || []}
                        onChange={(vals) => setFilter(f.key, vals)}
                      />
                    ))}
                  {sortColumnIndex != null &&
                    visibleColumns[sortColumnIndex]?.groupable && (
                      <Button
                        onClick={() => {
                          const base =
                            groupsOpenBase !== null
                              ? groupsOpenBase
                              : !report.collapseGroups;
                          setGroupsOpenBase(!base);
                          setToggledGroups(new Set());
                        }}
                      >
                        {(groupsOpenBase !== null
                          ? groupsOpenBase
                          : !report.collapseGroups)
                          ? "Colapsar todo"
                          : "Expandir todo"}
                      </Button>
                    )}
                  {(report.columns || []).length > 1 && (
                    <Popover
                      active={colsPopoverOpen}
                      activator={
                        <Button
                          icon={SettingsIcon}
                          onClick={() => setColsPopoverOpen((v) => !v)}
                        >
                          Columnas
                        </Button>
                      }
                      onClose={() => setColsPopoverOpen(false)}
                    >
                      <div style={{ padding: "0.75rem 1rem", minWidth: 220 }}>
                        <ChoiceList
                          allowMultiple
                          title="Mostrar columnas"
                          choices={(report.columns || []).map((c) => ({
                            label: c.label,
                            value: c.key,
                          }))}
                          selected={visibleColumns.map((c) => c.key)}
                          onChange={handleColsChange}
                        />
                      </div>
                    </Popover>
                  )}
                </div>
              </div>
              {displayRows.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center" }}>
                  <Text tone="subdued">Sin resultados.</Text>
                </div>
              ) : (() => {
                const cols = visibleColumns;
                const sortableMap = cols.map((c) => c.sortable !== false);
                const sortedRows = (() => {
                  if (sortColumnIndex == null) return displayRows;
                  const col = cols[sortColumnIndex];
                  if (!col) return displayRows;
                  const dir = sortDirection === "descending" ? -1 : 1;
                  const arr = [...displayRows];
                  arr.sort((a, b) => {
                    const va = a[col.key];
                    const vb = b[col.key];
                    // Column can pin a custom value order (e.g. status lifecycle)
                    // instead of numeric/alphabetical.
                    if (Array.isArray(col.sortOrder)) {
                      const rk = (v) => {
                        const i = col.sortOrder.indexOf(String(v ?? ""));
                        return i === -1 ? col.sortOrder.length : i;
                      };
                      return (rk(va) - rk(vb)) * dir;
                    }
                    const na = Number(va);
                    const nb = Number(vb);
                    const bothNum = !Number.isNaN(na) && !Number.isNaN(nb)
                      && va !== null && va !== undefined && va !== ""
                      && vb !== null && vb !== undefined && vb !== "";
                    if (bothNum) return (na - nb) * dir;
                    const sa = String(va ?? "");
                    const sb = String(vb ?? "");
                    return sa.localeCompare(sb) * dir;
                  });
                  return arr;
                })();
                // Auto-group: when the active sort column is flagged
                // `groupable`, collapse consecutive same-value rows under a
                // bold subheader (group label + count + subtotals for any
                // column marked `subtotal`). Sort is stable, so rows within a
                // group keep the RPC's secondary order.
                const groupCol =
                  sortColumnIndex != null && cols[sortColumnIndex]?.groupable
                    ? cols[sortColumnIndex]
                    : null;
                const subtotalCols = cols.filter((c) => c.subtotal);
                const baseOpen =
                  groupsOpenBase !== null ? groupsOpenBase : !report.collapseGroups;
                const isGroupOpen = (g) =>
                  toggledGroups.has(g) ? !baseOpen : baseOpen;
                const toggleGroup = (g) =>
                  setToggledGroups((prev) => {
                    const next = new Set(prev);
                    if (next.has(g)) next.delete(g);
                    else next.add(g);
                    return next;
                  });
                const cellStyleFor = (c) =>
                  c.width
                    ? c.truncate
                      ? {
                          width: c.width,
                          maxWidth: c.width,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }
                      : {
                          width: c.width,
                          maxWidth: c.width,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }
                    : undefined;
                const renderDataRow = (r, pos) => (
                  <IndexTable.Row id={`r-${pos}`} key={`r-${pos}`} position={pos}>
                    {cols.map((c) => {
                      const value = formatCell(c, r);
                      return (
                        <IndexTable.Cell key={c.key}>
                          <div
                            style={cellStyleFor(c)}
                            title={c.truncate ? String(value) : undefined}
                          >
                            <Text variant="bodySm">{value}</Text>
                          </div>
                        </IndexTable.Cell>
                      );
                    })}
                  </IndexTable.Row>
                );
                const body = [];
                if (!groupCol) {
                  sortedRows.forEach((r, i) => body.push(renderDataRow(r, i)));
                } else {
                  let pos = 0;
                  let i = 0;
                  while (i < sortedRows.length) {
                    const gval = sortedRows[i][groupCol.key];
                    const group = [];
                    while (
                      i < sortedRows.length &&
                      sortedRows[i][groupCol.key] === gval
                    ) {
                      group.push(sortedRows[i]);
                      i++;
                    }
                    const subtotals = {};
                    for (const c of subtotalCols)
                      subtotals[c.key] = group.reduce(
                        (s, r) => s + (Number(r[c.key]) || 0),
                        0
                      );
                    const gkey = String(gval ?? "");
                    const open = isGroupOpen(gkey);
                    body.push(
                      <IndexTable.Row
                        rowType="subheader"
                        id={`h-${pos}`}
                        key={`h-${pos}`}
                        position={pos}
                        onClick={() => toggleGroup(gkey)}
                      >
                        {cols.map((c) => {
                          let content = "";
                          if (c.key === groupCol.key)
                            content = `${open ? "▾" : "▸"}  ${gval || "—"} · ${
                              group.length
                            } ${groupCol.groupNoun || "fila"}${
                              group.length === 1 ? "" : "s"
                            }`;
                          else if (c.subtotal)
                            content = c.format
                              ? c.format(subtotals[c.key])
                              : subtotals[c.key];
                          return (
                            <IndexTable.Cell key={c.key}>
                              <div
                                style={cellStyleFor(c)}
                                title={
                                  c.key === groupCol.key ? String(content) : undefined
                                }
                              >
                                <Text variant="bodySm" fontWeight="semibold">
                                  {content}
                                </Text>
                              </div>
                            </IndexTable.Cell>
                          );
                        })}
                      </IndexTable.Row>
                    );
                    pos++;
                    if (open) for (const r of group) body.push(renderDataRow(r, pos++));
                  }
                }
                return (
                <IndexTable
                  resourceName={{ singular: "fila", plural: "filas" }}
                  itemCount={groupCol ? body.length : sortedRows.length}
                  selectable={false}
                  sortable={sortableMap}
                  sortColumnIndex={sortColumnIndex ?? undefined}
                  sortDirection={sortDirection}
                  onSort={(idx, dir) => { setSortColumnIndex(idx); setSortDirection(dir); }}
                  headings={cols.map((c) => ({ title: c.label }))}
                >
                  {body}
                </IndexTable>
                );
              })()}
            </LegacyCard>
          )}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Report;
